import { debounce } from '../../common/components/utils/utils';
import { GeolocationService } from '../utils/GeolocationService';

const SELECTORS = {
	inputWrapper: '[data-type="autocomplete-input-wrapper"]',
	list: '[data-auto="list"]',
	myLocationTrigger: '[data-auto-trigger="search-my-location"]',
	item: '[data-auto="dynamic"]',
	itemTitle: '[data-auto="title"]',
	itemSubtitle: '[data-auto="subtitle"]',
	itemInfo: '[data-auto="info"]',
	hiddenLocalisationInput: '[data-localisation-search]',
};

const CLASSES = {
	active: 'active',
	displayNone: 'display--none',
	itemRoot: 'autocomplete__item',
	itemMainInfo: 'autocomplete__item-main-info',
	itemSecondaryInfo: 'autocomplete__item-secondary-info',
};

const ITEM_TYPE = {
	country: 'country',
	city: 'city',
	clinic: 'clinic',
	noResults: 'no-results',
};

const ITEM_ICON = {
	location: 'location',
	clinic: 'clinic',
};

/**
 * Helper functions
 */

function createItemElement({ itemIcon, title, subtitle, info, url, onClickItem = () => {} }) {
	const item = document.createElement('li');
	item.dataset.auto = 'dynamic';

	let urlEle = null;
	if (url) {
		urlEle = document.createElement('a');
		urlEle.href = url;
	} else {
		item.addEventListener('click', onClickItem);
	}

	const root = urlEle || item;
	root.classList.add(CLASSES.itemRoot);

	const mainInfo = document.createElement('div');
	mainInfo.classList.add(CLASSES.itemMainInfo);

	if (itemIcon) {
		const iconStr = `
			<svg class="icon" role="img">
				<use xlink:href="#icon-${itemIcon}"></use>
			</svg>`;
		mainInfo.insertAdjacentHTML('afterbegin', iconStr);
	}

	// info
	const infoWrapper = document.createElement('div');
	const titleEle = document.createElement('p');
	titleEle.classList.add('m-0', 'mb-4');
	titleEle.textContent = title;
	const subtitleEle = document.createElement('small');
	subtitleEle.textContent = subtitle;

	infoWrapper.appendChild(titleEle);
	infoWrapper.appendChild(subtitleEle);

	mainInfo.appendChild(infoWrapper);

	root.appendChild(mainInfo);

	// secondary info
	const secondaryInfo = document.createElement('small');
	secondaryInfo.classList.add(CLASSES.itemSecondaryInfo);
	secondaryInfo.textContent = info;

	root.appendChild(secondaryInfo);

	if (url) {
		item.appendChild(root);
	}

	return item;
}

async function getResults({ term, minLen, locale }) {
	if (Number.isFinite(minLen) && minLen > term.length) {
		return [];
	}

	const {
		data: { data },
	} = await http.get(window.autocompleteLocationsURL, {
		params: {
			search: term,
			...(locale ? { locale } : {}),
		},
	});

	return data;
}

export class Autocomplete {
	constructor(autocompleteInput, autocompleteResults) {
		this.input = document.querySelector(autocompleteInput);
		this.inputWrapper = this.input.closest(SELECTORS.inputWrapper);
		this.results = document.querySelector(autocompleteResults);
		this.list = this.results.querySelector(SELECTORS.list);
		this.hiddenLocalisationInput = document.querySelector(SELECTORS.hiddenLocalisationInput);

		const requiredElements = [this.inputWrapper, this.input, this.results, this.list];
		if (requiredElements.some((ele) => !ele)) {
			return;
		}

		this.onInput = debounce(this.handleResults, 300);

		this.bindEvents();
		this.setup();
	}

	/**
	 * Bind `this` explicitly for event listeners.
	 */
	bindEvents() {
		['userLocationSuccess', 'getUserLocation', 'onInput', 'onClickDocument'].forEach((e) => {
			this[e] = this[e].bind(this);
		});
	}

	hideResults() {
		this.results.classList.remove(CLASSES.active);
	}

	showResults() {
		this.results.classList.add(CLASSES.active);
	}

	clearResults() {
		const items = [...this.list.querySelectorAll(SELECTORS.item)];
		items.forEach((item) => this.list.removeChild(item));
	}

	handleVisibilityResults({ term }) {
		this.clearResults();

		if (!term) {
			this.hideResults();
			return;
		}

		this.showResults();
	}

	addItemsResults({ results }) {
		let item;
		results.forEach((result) => {
			item = this.createItem({ ...result });
			this.list.appendChild(item);
		});

		/*
		if (!results.length) {
			item = this.createItem({ name: window.wordNoResults, type: ITEM_TYPE.noResults });
			item.setAttribute('disabled', true);
			this.list.appendChild(item);
		}
		*/
	}

	async handleResults(event) {
		const { target: input } = event;
		const term = input.value;
		const minLen = Number(input.dataset.autoMinLen);
		const locale = input.dataset.locale || '';

		this.handleVisibilityResults({ term });
		const results = await getResults({ term, minLen, locale });
		this.addItemsResults({ results });
	}

	createItem({ type, name, country, clinicsInfo, clinicsCount, location, url, defaultName }) {
		let item;

		if (type === ITEM_TYPE.country) {
			item = createItemElement({
				itemIcon: ITEM_ICON.location,
				title: name,
				subtitle: '',
				info: clinicsInfo || clinicsCount,
				url,
				onClickItem: () => this.onClickItem({ name, type, defaultName }),
			});
		}

		if (type === ITEM_TYPE.city) {
			item = createItemElement({
				itemIcon: ITEM_ICON.location,
				title: name,
				subtitle: country,
				info: clinicsInfo || clinicsCount,
				url,
				onClickItem: () => this.onClickItem({ name, type, defaultName }),
			});
		}

		if (type === ITEM_TYPE.clinic) {
			item = createItemElement({
				itemIcon: ITEM_ICON.clinic,
				title: name,
				subtitle: location,
				info: '',
				url,
				onClickItem: () => this.onClickItem({ name, type, defaultName }),
			});
		}

		if (type === ITEM_TYPE.noResults) {
			item = createItemElement({
				itemIcon: '',
				title: name,
				subtitle: '',
				info: '',
				url,
				onClickItem: () => {},
			});
		}

		return item;
	}

	onClickItem({ name, type, defaultName }) {
		this.input.value = name;
		if (this.hiddenLocalisationInput) {
			this.hiddenLocalisationInput.value = defaultName;
			if (type) this.hiddenLocalisationInput.name = type;
		} else {
			// eslint-disable-next-line no-lonely-if
			if (type) this.input.name = type;
		}
		this.hideResults();
	}

	onClickDocument(event) {
		const isClickInInput = this.inputWrapper.contains(event.target);
		const isClickInResults = this.results.contains(event.target);

		if (isClickInInput) {
			this.showResults();
			return;
		}

		if (isClickInResults) {
			return; // Click on every item in results is handled;
		}

		// Click outside of component
		this.hideResults();
	}

	/**
	 * If location share is allowed get the distance and fill the input.
	 * @param data
	 */
	userLocationSuccess(data) {
		this.input.value = data.address;
		this.hideResults();
	}

	/**
	 * When a user shares their location.
	 */
	getUserLocation() {
		if (!this.locationService) {
			this.locationService = new GeolocationService(this.userLocationSuccess);
		} else {
			this.locationService.getGeolocation();
		}
	}

	setupEventListeners() {
		this.input.addEventListener('input', this.onInput);
		document.addEventListener('click', this.onClickDocument);
		this.locationTriggerArr.forEach((ele) => ele.addEventListener('click', this.getUserLocation));
	}

	setup() {
		// In some case there could be 2 location triggers - one for mobile & one for desktop
		this.locationTriggerArr = [...document.querySelectorAll(SELECTORS.myLocationTrigger)];
		this.setupEventListeners();
	}
}
